import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { IconLoader2, IconShare } from "@tabler/icons-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { listBrokers } from "@/features/brokers/api";
import { cn } from "@/lib/utils/cn";
import { distributeProperty, PropertyActionError } from "../api";
import type { AgencyProperty, AssignmentMode } from "../api";

interface DistributePropertyModalProps {
  property: AgencyProperty | null;
  onOpenChange: (open: boolean) => void;
}

const MODES: AssignmentMode[] = ["exclusive", "pool"];

/**
 * Distribuir o imóvel da casa (hub#689). A escolha do corretor vem do roster — só quem tem
 * vínculo vivo pode receber — e a comissão só aparece (e só é exigida) quando há parceiro
 * externo na seleção, que é quando ela realmente significa alguma coisa.
 */
export function DistributePropertyModal({ property, onOpenChange }: DistributePropertyModalProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const [selected, setSelected] = useState<string[]>([]);
  const [mode, setMode] = useState<AssignmentMode>("exclusive");
  const [commission, setCommission] = useState("");

  const { data: brokers } = useQuery({
    queryKey: ["agency-brokers-list"],
    queryFn: listBrokers,
    enabled: property !== null,
  });

  // Convite ainda não aceito não tem conta canônica: não há carteira para receber o imóvel.
  const eligible = useMemo(
    () => (brokers ?? []).filter((b) => b.status === "active" || b.status === "pending_license"),
    [brokers],
  );

  const hasExternal = useMemo(
    () => eligible.some((b) => selected.includes(b.id) && b.linkType === "external"),
    [eligible, selected],
  );

  const distribute = useMutation({
    mutationFn: () =>
      distributeProperty(property!.id, {
        brokerIds: selected,
        mode,
        commissionPercentage: hasExternal ? Number(commission) : null,
      }),
    onSuccess: () => {
      toast.success(t("properties.toast.distributed"));
      close();
      queryClient.invalidateQueries({ queryKey: ["agency-properties"] });
    },
    onError: (error: Error) => {
      toast.error(
        error instanceof PropertyActionError
          ? t([`properties.errors.${error.code}`, "properties.errors.default"])
          : t("properties.errors.default"),
      );
    },
  });

  const close = () => {
    setSelected([]);
    setMode("exclusive");
    setCommission("");
    onOpenChange(false);
  };

  const toggle = (brokerId: string) =>
    setSelected((current) =>
      current.includes(brokerId)
        ? current.filter((id) => id !== brokerId)
        : [...current, brokerId],
    );

  const commissionValue = Number(commission);
  const commissionInvalid =
    hasExternal && (!commission || Number.isNaN(commissionValue) || commissionValue <= 0 || commissionValue >= 100);
  const canSubmit = selected.length > 0 && !commissionInvalid && !distribute.isPending;

  return (
    <Dialog open={property !== null} onOpenChange={(open) => (open ? null : close())}>
      <DialogContent className="sm:max-w-[520px]">
        <DialogHeader>
          <DialogTitle>{t("properties.distribute.title")}</DialogTitle>
          <DialogDescription>
            {property?.title} · {t("properties.distribute.desc")}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-2">
          <fieldset className="space-y-2">
            <legend className="text-sm font-bold text-ink">
              {t("properties.distribute.modeLabel")}
            </legend>
            <div className="grid gap-2 sm:grid-cols-2">
              {MODES.map((value) => (
                <label
                  key={value}
                  className={cn(
                    "flex cursor-pointer flex-col gap-1 rounded-xl border p-3 transition-all",
                    mode === value
                      ? "border-brand-500 bg-brand-grad-soft"
                      : "border-border bg-gray-50 hover:border-brand-300",
                  )}
                >
                  <span className="flex items-center gap-2 text-sm font-bold text-ink">
                    <input
                      type="radio"
                      name="assignment-mode"
                      className="sr-only"
                      checked={mode === value}
                      onChange={() => setMode(value)}
                    />
                    {t(`properties.distribute.mode.${value}`)}
                  </span>
                  <span className="text-xs text-ink-muted">
                    {t(`properties.distribute.mode.${value}Desc`)}
                  </span>
                </label>
              ))}
            </div>
          </fieldset>

          <fieldset className="space-y-2">
            <legend className="text-sm font-bold text-ink">
              {t("properties.distribute.brokersLabel")}
            </legend>
            {eligible.length === 0 ? (
              <p className="text-xs text-ink-muted">{t("properties.distribute.noBrokers")}</p>
            ) : (
              <div className="max-h-56 space-y-1.5 overflow-y-auto rounded-xl border border-border p-2">
                {eligible.map((broker) => (
                  <label
                    key={broker.id}
                    className="flex cursor-pointer items-center gap-3 rounded-lg px-2 py-2 hover:bg-gray-50"
                  >
                    <input
                      type="checkbox"
                      checked={selected.includes(broker.id)}
                      onChange={() => toggle(broker.id)}
                      className="h-4 w-4 rounded border-border text-brand-600"
                    />
                    <span className="flex-1 text-sm font-medium text-ink">{broker.name}</span>
                    <span className="text-[10px] font-black uppercase tracking-wider text-ink-soft">
                      {broker.linkType
                        ? t(`brokers.linkType.${broker.linkType}`)
                        : t("brokers.roster.role")}
                    </span>
                  </label>
                ))}
              </div>
            )}
          </fieldset>

          {hasExternal && (
            <div className="space-y-2">
              <label htmlFor="commission" className="text-sm font-bold text-ink">
                {t("properties.distribute.commissionLabel")}
              </label>
              <input
                id="commission"
                type="number"
                min={0.01}
                max={99.99}
                step={0.5}
                value={commission}
                onChange={(event) => setCommission(event.target.value)}
                className="w-full rounded-xl border border-border bg-gray-50 px-4 py-2.5 text-sm outline-none transition-all focus:border-brand-500 focus:bg-white focus:ring-4 focus:ring-brand-500/10"
              />
              <p className="text-xs text-ink-muted">{t("properties.distribute.commissionHelp")}</p>
            </div>
          )}
        </div>

        <DialogFooter>
          <button
            type="button"
            onClick={close}
            className="rounded-xl px-4 py-2 text-sm font-bold text-ink-muted transition-colors hover:bg-gray-100"
          >
            {t("properties.distribute.cancel")}
          </button>
          <button
            type="button"
            disabled={!canSubmit}
            onClick={() => distribute.mutate()}
            className={cn(
              "inline-flex items-center gap-2 rounded-xl px-6 py-2 text-sm font-black uppercase tracking-wider text-white shadow-soft transition-all",
              "bg-brand-grad hover:scale-105 hover:shadow-glow active:scale-95 disabled:opacity-50",
            )}
          >
            {distribute.isPending ? (
              <IconLoader2 size={18} className="animate-spin" />
            ) : (
              <IconShare size={18} />
            )}
            {t("properties.distribute.submit")}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
